<script>
  import { onMount } from "svelte";
  import mediumZoom from "medium-zoom";
  import { GQLNow } from "gql-now";
  import {
    Page,
    Navbar,
    NavLeft,
    NavRight,
    Link,
    Icon,
    Badge,
    Popup,
    Block,
    Button,
    f7,
    f7ready,
  } from "framework7-svelte";
  import DocumentItem from "./DocumentItem.svelte";
  import AccountStatusControl from "./AccountStatusControl.svelte";
  import SignupProgressBar from "./SignupProgressBar.svelte";
  import AssignUccTagModal from "./AssignUccTagModal.svelte";
  import StatusBadge from "./common/StatusBadge.svelte";

  import { adminMutation, orgMutation } from "../services/adminGQL";
  import {
    calculateDematAccount,
    isValidDemat,
    formatDematDisplay,
    FCML_DP_ID,
    FCML_DP_NAME,
    DEPOSITORY_NAME,
  } from "../utils/dematHelper";
  import { safeDecodeString, recursiveUrlDecode, formatDecodedAddress } from "../utils/urlDecoder";

  export let f7route;
  export let f7router;
  export let pageTitle = "User Details";
  export let primaryStorageKey = "user_data";

  let userParam;
  let user_details = {};
  let pan_front = "";
  let signature_image = "";
  let profile_image = "";
  let cheque_image = "";
  let aadhaar_doc = "";
  let agreement_doc = "";
  let annualIncome = "";
  let dob = "";
  let nominees = [];
  let dossierModalOpened = false;

  // Card 2 fields
  let demat_id = "";
  let ucc = "";
  let account_status = "pending";
  let isSavingDematId = false;
  let isUpdatingStatus = false;
  let isDeletingUser = false;
  let assignModalOpened = false;

  $: isAgreementSigned = Boolean(
    user_details.agreement_status === "signed" ||
    user_details.agreement_status === "completed" ||
    (user_details.doc_data && (user_details.doc_data.agreement_status === "signed" || user_details.doc_data.agreement_status === "completed")) ||
    user_details.is_agreement_signed === true ||
    user_details.account_status === "approved" ||
    (Array.isArray(user_details.workflow_steps) && user_details.workflow_steps.some(s => s.name === "agreement_doc_generate_and_sign" && (s.status === "completed" || s.status === "verified")))
  );

  $: userInitials = getUserInitials(user_details.fullName || user_details.firstName || user_details.name || "");

  function getUserInitials(name) {
    if (!name || typeof name !== "string") return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  onMount(() => {
    f7ready(() => {
      userParam = f7route.query.user;
      resolveUser(userParam, primaryStorageKey);

      mediumZoom(".zoom", {
        margin: 50,
        background: "#000",
      });
      mediumZoom(".zoom-tmp", {
        margin: 24,
        background: "rgba(0,0,0,0.85)",
        scrollOffset: 0,
        container: "#zoom-container",
        template: "#zoom-template",
      });
    });
  });

  function safeDecodeDocUrl(val) {
    if (!val || typeof val !== "string" || val.trim() === "" || val === "undefined" || val === "null") {
      return "";
    }
    let str = val.trim().replace(/^["']|["']$/g, "");
    // Only decode protocol if double-encoded (e.g. starts with http%3A or https%3A)
    // NEVER decode query params because AWS S3 signatures use %2F etc.
    if (str.startsWith("http%3A") || str.startsWith("https%3A")) {
      try {
        str = decodeURIComponent(str);
      } catch (e) {}
    }
    return str;
  }

  function safeDecodeText(val) {
    return safeDecodeString(val);
  }

  function resolveUser(param, storageKey) {
    let dataset = [];
    try {
      dataset = JSON.parse(localStorage.getItem(storageKey)) || [];
    } catch (e) {
      dataset = [];
    }

    let globalDataset = [];
    try {
      globalDataset = JSON.parse(localStorage.getItem("user_data")) || [];
    } catch (e) {
      globalDataset = [];
    }

    let resolved = null;

    if (param !== undefined && param !== null && param !== "") {
      resolved = dataset.find(
        (u) =>
          u &&
          (u.mobile === param ||
            u.user_id === param ||
            u._id === param ||
            u.pan === param)
      );

      if (!resolved && !isNaN(param) && dataset[parseInt(param, 10)]) {
        resolved = dataset[parseInt(param, 10)];
      }

      if (!resolved) {
        resolved = globalDataset.find(
          (u) =>
            u &&
            (u.mobile === param ||
              u.user_id === param ||
              u._id === param ||
              u.pan === param)
        );
      }

      if (!resolved && !isNaN(param) && globalDataset[parseInt(param, 10)]) {
        resolved = globalDataset[parseInt(param, 10)];
      }
    }

    user_details = resolved || {};
    cleanObject(user_details);

    pan_front = safeDecodeDocUrl(user_details.pan_front);
    signature_image = safeDecodeDocUrl(user_details.signature_image);
    profile_image = safeDecodeDocUrl(user_details.profile_image);
    cheque_image = safeDecodeDocUrl(
      user_details.cheque_image ||
      user_details.bank_proof ||
      user_details.cancelled_cheque ||
      user_details.bank_statement
    );
    aadhaar_doc = safeDecodeDocUrl(
      user_details.aadhaar_url ||
      user_details.aadhaar_file ||
      user_details.digio_aadhaar_file ||
      user_details.aadhaar_image
    );
    agreement_doc = safeDecodeDocUrl(
      user_details.agreement_url ||
      user_details.signed_agreement_file ||
      user_details.agreement_doc ||
      user_details.agreement_file
    );
    annualIncome = safeDecodeText(user_details.annualIncome);
    dob = safeDecodeText(user_details.dob);
    nominees = Array.isArray(user_details.nominees) ? user_details.nominees : [];

    demat_id = user_details.demat_acc_number || user_details.demat_id || "";
    ucc = user_details.ucc || user_details.client_tag || "";
    account_status = (user_details.account_status || "pending").toLowerCase();
  }

  function cleanObject(obj) {
    if (!obj || typeof obj !== "object") return;
    for (const key of Object.getOwnPropertyNames(obj)) {
      if (obj[key] === null || obj[key] === undefined) {
        obj[key] = "";
      }
    }
  }

  async function handleStatusChange(e) {
    const { newStatus } = e.detail;
    isUpdatingStatus = true;
    try {
      user_details.account_status = newStatus;
      user_details.approval_status = newStatus;
      account_status = newStatus;
      syncLocalStorageUser(user_details);

      const userId = user_details.user_id || user_details._id;
      const calculatedDemat = calculateDematAccount(
        user_details.demat_acc_number || user_details.trdr_id || user_details.demat_id || user_details.ucc
      );

      // 1. Synchronize trdr-org-services with depository settings
      try {
        await orgMutation(
          "update_member_data",
          {
            user_id: userId,
            mobile: user_details.mobile,
            account_status: newStatus,
            approval_status: newStatus,
            depository_name: DEPOSITORY_NAME,
            depository_participant: FCML_DP_NAME,
            dp_id: FCML_DP_ID,
            demat_acc_number: calculatedDemat || undefined,
            ucc: user_details.ucc || ucc,
          },
          "status _id mobile approval_status"
        );
      } catch (orgErr) {
        console.warn("Backend org status update warning:", orgErr);
      }

      // 2. Synchronize trdr-admin-services
      if (userId) {
        try {
          await adminMutation(
            "update_signup_application",
            {
              user_id: userId,
              account_status: newStatus,
              approval_status: newStatus,
              demat_acc_number: calculatedDemat || undefined,
            },
            "status user_id"
          );
        } catch (adminErr) {
          console.warn("Backend admin status update warning:", adminErr);
        }
      }

      f7.toast.create({
        text: `✓ Account status updated to "${newStatus.toUpperCase()}"`,
        closeTimeout: 3000,
      }).open();
    } catch (err) {
      console.error("Account status update error:", err);
      f7.dialog.alert(err.message || "Failed to update account status", "Error");
    } finally {
      isUpdatingStatus = false;
    }
  }

  function handleQuickApprove() {
    f7.dialog.create({
      title: "Approve Account",
      content: `
        <div style="text-align: center; padding: 6px 0;">
          <p style="margin-bottom: 8px; font-size: 14px; color: #1e293b;">
            Are you sure you want to approve the account for <strong>${user_details.fullName || user_details.mobile}</strong>?
          </p>
          <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 10px;">
            <span style="background: #ed6c02; color: white; padding: 3px 8px; border-radius: 10px; font-size: 11px; font-weight: bold;">
              PENDING
            </span>
            <span style="font-weight: bold; color: #64748b;">➔</span>
            <span style="background: #16a34a; color: white; padding: 3px 8px; border-radius: 10px; font-size: 11px; font-weight: bold;">
              APPROVED
            </span>
          </div>
        </div>
      `,
      buttons: [
        { text: "Cancel", color: "gray" },
        {
          text: "Confirm Approve",
          bold: true,
          color: "green",
          onClick: () => {
            handleStatusChange({ detail: { previousStatus: "pending", newStatus: "approved" } });
          },
        },
      ],
    }).open();
  }

  function handleSaveDematId() {
    const raw = (demat_id || "").trim();
    if (!raw) {
      f7.dialog.alert("Please enter a valid Demat Account Number or DP Client ID.", "Demat ID Required");
      return;
    }

    const calculated = calculateDematAccount(raw);

    f7.dialog.confirm(
      `Save 16-digit Demat Account "${formatDematDisplay(calculated)}" for ${user_details.fullName || user_details.mobile}?`,
      "Confirm Demat ID",
      () => {
        submitSaveDematId(calculated);
      }
    );
  }

  async function submitSaveDematId(newDematId) {
    isSavingDematId = true;
    try {
      const calculatedDemat = calculateDematAccount(newDematId);
      user_details.demat_acc_number = calculatedDemat;
      user_details.demat_id = calculatedDemat;
      demat_id = calculatedDemat;
      syncLocalStorageUser(user_details);

      const userId = user_details.user_id || user_details._id;

      // 1. Update trdr-org-services depository record
      try {
        await orgMutation(
          "update_member_data",
          {
            user_id: userId,
            mobile: user_details.mobile,
            depository_name: DEPOSITORY_NAME,
            depository_participant: FCML_DP_NAME,
            dp_id: FCML_DP_ID,
            demat_acc_number: calculatedDemat,
            ucc: user_details.ucc || ucc,
          },
          "status _id demat_acc_number"
        );
      } catch (orgErr) {
        console.warn("org_services Demat update notice:", orgErr);
      }

      // 2. Update trdr-admin-services
      if (userId) {
        try {
          await adminMutation(
            "update_signup_application",
            {
              user_id: userId,
              demat_acc_number: calculatedDemat,
            },
            "status user_id"
          );
        } catch (adminErr) {
          console.warn("Backend Demat ID update warning:", adminErr);
        }
      }

      f7.toast.create({
        text: `✓ Demat ID updated to ${formatDematDisplay(calculatedDemat)}`,
        closeTimeout: 3000,
      }).open();
    } catch (err) {
      console.error("Save Demat ID error:", err);
      f7.dialog.alert(err.message || "Failed to save Demat ID", "Error");
    } finally {
      isSavingDematId = false;
    }
  }

  function generateDossierData() {
    const finalDemat = calculateDematAccount(
      user_details.demat_acc_number || user_details.trdr_id || user_details.demat_id || user_details.ucc
    );

    const permAddr = formatDecodedAddress(user_details.permanentAddress);
    const commAddr = formatDecodedAddress(user_details.communicationAddress);

    return {
      ucc: user_details.ucc || "PENDING",
      client_name: user_details.fullName || "-",
      mobile: user_details.mobile || "-",
      email: user_details.emailAddress || user_details.email || "-",
      pan: user_details.pan || "-",
      dob: dob || user_details.dob || "-",
      gender: user_details.gender || "-",
      father_name: user_details.fathersName || "-",
      mother_name: user_details.mothersName || "-",
      marital_status: user_details.martialStatus || "-",
      occupation: user_details.occupationType || "-",
      annual_income: annualIncome || "-",
      permanent_address: permAddr,
      communication_address: commAddr,
      bank_name: user_details.bank_name || "-",
      account_number: user_details.account_number || "-",
      account_type: user_details.account_type || "-",
      ifsc: user_details.ifsc || "-",
      penny_drop_status: user_details.penny_drop_status || "PENDING",
      demat_account: finalDemat,
      depository: DEPOSITORY_NAME,
      dp_name: FCML_DP_NAME,
      dp_id: FCML_DP_ID,
      agreement_signed: isAgreementSigned,
      generated_at: new Date().toISOString(),
    };
  }

  function downloadDossierTxt() {
    const d = generateDossierData();
    const content = `=======================================================
TRDR / FEDERAL CAPITAL MARKETS LIMITED (FCML)
CLIENT ONBOARDING & KYC DOSSIER SUMMARY
=======================================================
Generated At: ${d.generated_at}

1. CLIENT IDENTIFIERS:
-------------------------------------------------------
Full Name           : ${d.client_name}
UCC                 : ${d.ucc}
PAN                 : ${d.pan}
Mobile Number       : +91 ${d.mobile}
Email Address       : ${d.email}

2. DEPOSITORY & DEMAT PROVISIONING:
-------------------------------------------------------
Depository          : ${d.depository}
Participant Name    : ${d.dp_name}
DP ID               : ${d.dp_id}
16-Digit Demat No   : ${formatDematDisplay(d.demat_account)}

3. BANKING & PENNY DROP:
-------------------------------------------------------
Bank Name           : ${d.bank_name}
Account Number      : ${d.account_number} (${d.account_type})
IFSC Code           : ${d.ifsc}
Penny Drop Status   : ${d.penny_drop_status}

4. DEMOGRAPHIC & ADDRESS DETAILS:
-------------------------------------------------------
Date of Birth       : ${d.dob}
Gender              : ${d.gender}
Father's Name       : ${d.father_name}
Mother's Name       : ${d.mother_name}
Marital Status      : ${d.marital_status}
Occupation          : ${d.occupation}
Annual Income       : ${d.annual_income}
Permanent Address   : ${d.permanent_address}
Current Address     : ${d.communication_address}

5. COMPLIANCE & ATTACHMENT MANIFEST:
-------------------------------------------------------
[${d.pan ? 'X' : ' '}] PAN Card Proof Verified
[${d.account_number ? 'X' : ' '}] Bank Proof / Cheque Verified
[${d.agreement_signed ? 'X' : ' '}] Digio Signed KYC Agreement Complete
=======================================================`;

    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FCML_KYC_Dossier_${d.ucc || d.mobile}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function downloadDossierJson() {
    const d = generateDossierData();
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FCML_KYC_Dossier_${d.ucc || d.mobile}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function forwardToBrokerDesk() {
    const d = generateDossierData();
    const subject = encodeURIComponent(`[FCML KYC Dossier] Onboarding - ${d.client_name} (UCC: ${d.ucc})`);
    const body = encodeURIComponent(`Dear FCML Operations Desk,

Please find the verified KYC Onboarding Dossier for client:

* Client Name: ${d.client_name}
* UCC: ${d.ucc}
* PAN: ${d.pan}
* Mobile: +91 ${d.mobile}
* 16-Digit CDSL Demat: ${formatDematDisplay(d.demat_account)}
* Bank: ${d.bank_name} (${d.ifsc}) - Acc: ${d.account_number}
* Address: ${d.permanent_address}

Verified KYC Attachments:
1. PAN Card
2. Bank Cheque / Penny Drop (Status: ${d.penny_drop_status})
3. Digio Signed Client Agreement (Status: ${d.agreement_signed ? 'Signed' : 'Pending'})

Regards,
TRDR Admin Operations
desk.trdr.money`);

    window.open(`mailto:admin@trdr.money,ricky@trdr.money?subject=${subject}&body=${body}`, "_blank");
  }

  function handleUccAssigned(e) {
    const newTag = e.detail.tag;
    user_details.ucc = newTag;
    user_details.client_tag = newTag;
    ucc = newTag;
    syncLocalStorageUser(user_details);
  }

  function handleDeleteUser() {
    f7.dialog.create({
      title: "Delete User Account",
      content: `
        <div style="font-size: 13px; color: #212529;">
          <p>Are you sure you want to delete user <strong>${user_details.fullName || user_details.mobile}</strong>?</p>
          <div style="margin-top: 10px; padding: 8px; background: #fff3cd; border-radius: 6px; border: 1px solid #ffeeba; font-size: 11px; color: #856404; line-height: 1.4;">
            <strong>Compliance Notice:</strong> In accordance with financial regulatory guidelines (SEBI/BSE), user identity and audit records are soft-deleted and archived, rather than purged from the system.
          </div>
        </div>
      `,
      buttons: [
        { text: "Cancel", color: "gray" },
        {
          text: "Confirm Delete",
          bold: true,
          color: "red",
          onClick: () => {
            submitSoftDeleteUser();
          },
        },
      ],
    }).open();
  }

  async function submitSoftDeleteUser() {
    isDeletingUser = true;
    try {
      user_details.account_status = "deleted";
      user_details.is_deleted = true;
      user_details.deleted_at = Date.now();
      syncLocalStorageUser(user_details);

      const token = localStorage.getItem("token") || "";
      const orgId = localStorage.getItem("org_id") || "60e6cd9df9c32f00072b71f8";

      if (token && user_details.user_id) {
        try {
          const obj = new GQLNow("https://api.trdr.money/graphql/trdr-admin-services", {
            "x-trdr-org": orgId,
            "x-access-token": token,
          });
          await obj.query(
            "update_signup_application",
            {
              user_id: user_details.user_id,
              account_status: "deleted",
              is_deleted: true,
            },
            "status user_id"
          );
        } catch (gqlErr) {
          console.warn("Backend soft-delete warning:", gqlErr);
        }
      }

      f7.toast.create({
        text: `✓ User account soft-deleted and archived`,
        closeTimeout: 3000,
      }).open();

      setTimeout(() => {
        f7router.back();
      }, 1000);
    } catch (err) {
      console.error("Soft-delete error:", err);
      f7.dialog.alert(err.message || "Failed to soft delete user", "Error");
    } finally {
      isDeletingUser = false;
    }
  }

  function syncLocalStorageUser(updatedUser) {
    const keys = ["user_data", "approved_user_data", "pending_user_data", "incomplete_user_data"];
    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const list = JSON.parse(raw);
        if (!Array.isArray(list)) continue;
        const idx = list.findIndex(
          (u) =>
            u &&
            (u.mobile === updatedUser.mobile ||
              u.user_id === updatedUser.user_id ||
              u._id === updatedUser._id)
        );
        if (idx !== -1) {
          list[idx] = { ...list[idx], ...updatedUser };
          localStorage.setItem(key, JSON.stringify(list));
        }
      } catch (e) {}
    }
  }
</script>

<Page class="admin-home user-details-page font-montserrat">
  <Navbar
    title={pageTitle}
    backLink={true}
    style="--f7-navbar-bg-color: #ffffff; --f7-navbar-text-color: #0f172a; --f7-navbar-link-color: #0284c7;"
    sliding={false}
  >
    <NavLeft />
    <NavRight>
      <Link class="hide-on-mobile">
        <span class="url-badge">desk.trdr.money</span>
      </Link>
      <Link iconOnly>
        <Icon ios="f7:bell_fill" aurora="f7:bell_fill" md="f7:bell_fill">
          <Badge color="blue">5</Badge>
        </Icon>
      </Link>
      <Link iconIos="f7:person_alt_circle" iconAurora="f7:person_alt_circle" iconMd="f7:person_alt_circle" />
    </NavRight>
  </Navbar>

  <div class="user-details-scroll-content">
    <div class="user-details-container">
      
      <!-- Top Grid: 3 Responsive Cards (Overview, UCC status, Personal Details) -->
      <div class="top-cards-grid">
        
        <!-- 1. Overview Card -->
        <div class="modern-card overview-card">
          <div class="card-body-styled">
            <div class="overview-header-row">
              <div class="avatar-container">
                {#if profile_image}
                  <!-- svelte-ignore a11y-missing-attribute -->
                  <img
                    class="zoom-tmp avatar-img"
                    alt="User Photo"
                    src={profile_image}
                  />
                {:else}
                  <div class="avatar-fallback">
                    {userInitials}
                  </div>
                {/if}
              </div>

              <div class="overview-title-col">
                <h2 class="user-name-heading" title={user_details.fullName || 'No Name'}>
                  {user_details.fullName || 'No Name'}
                </h2>
                <div class="overview-subinfo">
                  <span class="subinfo-item">
                    <Icon f7="phone" size="12" style="margin-right: 3px; color: #64748b;" />
                    {user_details.mobile || '—'}
                  </span>
                  {#if user_details.pan}
                    <span class="subinfo-item">
                      <span class="mini-tag">PAN</span>
                      {user_details.pan}
                    </span>
                  {/if}
                </div>
              </div>
            </div>

            <div class="overview-controls-list">
              <!-- Account Status Row -->
              <div class="control-row">
                <span class="control-label">Account Status</span>
                <div class="control-action">
                  <AccountStatusControl
                    status={user_details.account_status || account_status}
                    userName={user_details.fullName || user_details.mobile}
                    on:change={handleStatusChange}
                    disabled={isUpdatingStatus}
                  />
                </div>
              </div>

              <!-- Quick Approve Button if Pending -->
              {#if (user_details.account_status || account_status || "").toLowerCase() === "pending"}
                <div class="quick-approve-container">
                  <button
                    type="button"
                    class="quick-approve-button"
                    on:click={handleQuickApprove}
                    disabled={isUpdatingStatus}
                  >
                    <Icon f7="checkmark_seal_fill" size="15" style="margin-right: 6px;" />
                    Mark as APPROVED
                  </button>
                </div>
              {/if}

              <!-- KYC Agreement Status -->
              <div class="control-row">
                <span class="control-label">KYC Agreement</span>
                <div class="control-action">
                  {#if isAgreementSigned}
                    <span class="status-badge signed">
                      <Icon f7="checkmark_alt" size="12" style="margin-right: 4px;" /> Signed
                    </span>
                  {:else}
                    <span class="status-badge unsigned">
                      <Icon f7="xmark" size="12" style="margin-right: 4px;" /> Not Signed
                    </span>
                  {/if}
                </div>
              </div>

              <!-- Signup Progress Bar -->
              <SignupProgressBar
                user={user_details}
                agreementSigned={isAgreementSigned}
              />

              <!-- Soft Delete Action -->
              <div class="danger-zone-footer">
                <button
                  type="button"
                  class="soft-delete-btn"
                  on:click={handleDeleteUser}
                  disabled={isDeletingUser}
                >
                  <Icon f7="trash" size="13" style="margin-right: 5px;" />
                  {isDeletingUser ? 'Deleting...' : 'Delete User (Soft Delete)'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. UCC Status Card -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box blue">
                <Icon f7="tag_fill" size="14" color="blue" />
              </span>
              <span>UCC Status</span>
            </div>
          </div>
          <div class="card-body-styled">
            <div class="kv-list">
              <div class="kv-item">
                <span class="kv-label">TRDR ID</span>
                <span class="kv-value">
                  <span class="monospace-pill">
                    {user_details.trdr_id || user_details.dp_acc_no || '—'}
                  </span>
                </span>
              </div>

              <div class="kv-item stacked margin-top-half">
                <span class="kv-label">Demat Account Number</span>
                <div class="demat-input-group">
                  <input
                    type="text"
                    class="demat-input"
                    placeholder="16-digit Demat ID"
                    bind:value={demat_id}
                  />
                  <button
                    type="button"
                    class="save-demat-btn"
                    on:click={handleSaveDematId}
                    disabled={isSavingDematId}
                  >
                    {isSavingDematId ? '...' : 'Save'}
                  </button>
                </div>
              </div>

              <div class="kv-item margin-top-half" style="align-items: center;">
                <span class="kv-label">UCC Tag</span>
                <div class="ucc-tag-actions">
                  {#if user_details.ucc || user_details.client_tag}
                    <span class="ucc-active-badge">
                      {user_details.ucc || user_details.client_tag}
                    </span>
                  {:else}
                    <span class="ucc-unassigned-badge">
                      <Icon f7="exclamationmark_circle_fill" size="11" color="red" />
                      Unassigned
                    </span>
                  {/if}
                  <button
                    type="button"
                    class="manage-tag-btn"
                    on:click={() => { assignModalOpened = true; }}
                  >
                    Manage Tag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Personal Details Card -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box purple">
                <Icon f7="person_crop_circle" size="14" color="purple" />
              </span>
              <span>Personal Details</span>
            </div>
          </div>
          <div class="card-body-styled">
            <div class="kv-list">
              <div class="kv-item">
                <span class="kv-label">Full Name</span>
                <span class="kv-value">{user_details.fullName || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Gender</span>
                <span class="kv-value">{user_details.gender || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Date of Birth</span>
                <span class="kv-value">{dob || '—'}</span>
              </div>
              <div class="kv-item stacked">
                <span class="kv-label">Email</span>
                <span class="kv-value email-value">
                  {user_details.emailAddress || '—'}
                  {#if user_details.email_verification || user_details.emailAddress}
                    <Icon f7="checkmark_circle_fill" size="13" color="blue" style="margin-left: 4px; vertical-align: middle;" />
                  {/if}
                </span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Mobile</span>
                <span class="kv-value">
                  {user_details.mobile || '—'}
                  <Icon f7="checkmark_circle_fill" size="13" color="blue" style="margin-left: 4px; vertical-align: middle;" />
                </span>
              </div>
              {#if user_details.latitude && user_details.longitude}
                <div class="kv-item">
                  <span class="kv-label">Geo Location</span>
                  <span class="kv-value">
                    <a
                      href="https://maps.google.com/?q={user_details.latitude},{user_details.longitude}"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="external geo-link"
                      external
                    >
                      <Icon f7="location_fill" size="12" style="margin-right: 2px;" />
                      {user_details.latitude}, {user_details.longitude}
                    </a>
                  </span>
                </div>
              {/if}
            </div>
          </div>
        </div>

      </div>

      <!-- Middle Grid: 4 Modern Responsive Cards (Address, Additional, Bank, Documents) -->
      <!-- Responsive: 1 column on mobile, 2 columns on tablet, 4 columns on large desktop -->
      <div class="details-cards-grid">
        
        <!-- 1. Address Details -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box green">
                <Icon f7="placemark_fill" size="14" color="green" />
              </span>
              <span>Address Details</span>
            </div>
            <Icon f7="checkmark_circle_fill" size="16" color="blue" />
          </div>
          <div class="card-body-styled">
            <div class="kv-list">
              <div class="kv-item">
                <span class="kv-label">Status</span>
                <span class="kv-value">{user_details.residentialStatus || '—'}</span>
              </div>
              <div class="kv-item stacked">
                <span class="kv-label">Permanent Address</span>
                <span class="kv-value address-text">{formatDecodedAddress(user_details.permanentAddress)}</span>
              </div>
              <div class="kv-item stacked">
                <span class="kv-label">Current Address</span>
                <span class="kv-value address-text">{formatDecodedAddress(user_details.communicationAddress)}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Additional Details -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box orange">
                <Icon f7="doc_plaintext" size="14" color="orange" />
              </span>
              <span>Additional Details</span>
            </div>
          </div>
          <div class="card-body-styled">
            <div class="kv-list">
              <div class="kv-item">
                <span class="kv-label">Father's Name</span>
                <span class="kv-value">{user_details.fathersName || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Mother's Name</span>
                <span class="kv-value">{user_details.mothersName || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Marital Status</span>
                <span class="kv-value">{user_details.martialStatus || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Occupation</span>
                <span class="kv-value">{user_details.occupationType || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Annual Income</span>
                <span class="kv-value">{annualIncome || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">PEP Status</span>
                <span class="kv-value">{user_details.isPoliticalyExposed ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Bank Details -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box teal">
                <Icon f7="building_2_fill" size="14" color="teal" />
              </span>
              <span>Bank Details</span>
            </div>
            <Icon f7="checkmark_circle_fill" size="16" color="blue" />
          </div>
          <div class="card-body-styled">
            <div class="kv-list">
              <div class="kv-item">
                <span class="kv-label">Account Type</span>
                <span class="kv-value">{user_details.account_type || '—'}</span>
              </div>
              <div class="kv-item stacked">
                <span class="kv-label">Account Number</span>
                <span class="kv-value">
                  <span class="monospace-pill">{user_details.account_number || '—'}</span>
                </span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Bank Name</span>
                <span class="kv-value">{user_details.bank_name || '—'}</span>
              </div>
              <div class="kv-item">
                <span class="kv-label">IFSC Code</span>
                <span class="kv-value">
                  <span class="monospace-pill">{user_details.ifsc || '—'}</span>
                </span>
              </div>
              <div class="kv-item">
                <span class="kv-label">Penny Drop</span>
                <span class="kv-value">
                  {#if String(user_details.penny_drop_status).toLowerCase() === 'true' || String(user_details.penny_drop_status).toLowerCase() === 'success' || String(user_details.penny_drop_status).toLowerCase() === 'completed'}
                    <span class="status-badge signed">✓ Verified</span>
                  {:else}
                    <span class="status-badge pending">{user_details.penny_drop_status || 'Pending'}</span>
                  {/if}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Upload Documents -->
        <div class="modern-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box blue">
                <Icon f7="folder_badge_person_crop" size="14" color="blue" />
              </span>
              <span>Upload Documents</span>
            </div>
          </div>
          <div class="card-body-styled documents-card-body">
            <div class="documents-list-wrapper">
              <DocumentItem
                title="PAN Card"
                url={pan_front}
                docType="pan"
              />
              <DocumentItem
                title="Bank Cheque / Proof"
                url={cheque_image}
                docType="cheque"
                pennyDropStatus={user_details.penny_drop_status}
                pennyDropDetails={user_details.penny_drop_response}
                accountDetails={{
                  account_number: user_details.account_number,
                  ifsc: user_details.ifsc,
                  bank_name: user_details.bank_name,
                  account_type: user_details.account_type
                }}
              />
              <DocumentItem
                title="Signature"
                url={signature_image}
                docType="signature"
              />
              <DocumentItem
                title="Selfie / Photo"
                url={profile_image}
                docType="profile"
              />
              {#if aadhaar_doc}
                <DocumentItem
                  title="Digio Aadhaar / eKYC"
                  url={aadhaar_doc}
                  docType="general"
                />
              {/if}
              {#if agreement_doc}
                <DocumentItem
                  title="Signed Client Agreement"
                  url={agreement_doc}
                  docType="general"
                />
              {/if}
            </div>
          </div>
        </div>

        <!-- 5. FCML Broker KYC Desk & Dossier Generator (Task 7) -->
        <div class="modern-card broker-desk-card">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box purple">
                <Icon f7="briefcase_fill" size="14" color="purple" />
              </span>
              <span>FCML Broker KYC Desk (Task 7)</span>
            </div>
            <span class="desk-badge">Zero-Trust Active</span>
          </div>
          <div class="card-body-styled">
            <p class="broker-desk-desc">
              Generate standardized client onboarding dossiers with zero external PII leakage, or forward verified eKYC packages directly to the FCML broker desk.
            </p>
            <div class="broker-actions-grid">
              <Button fill color="blue" small on:click={() => (dossierModalOpened = true)}>
                <span>📄 View & Export KYC Dossier</span>
              </Button>
              <Button fill color="green" small on:click={forwardToBrokerDesk}>
                <span>✉️ Forward to Broker Desk</span>
              </Button>
            </div>
          </div>
        </div>

      </div>

      <!-- Nominee Details (if present) -->
      {#if nominees && nominees.length > 0}
        <div class="modern-card margin-top">
          <div class="card-header-styled">
            <div class="header-title-flex">
              <span class="header-icon-box purple">
                <Icon f7="person_3_fill" size="14" color="purple" />
              </span>
              <span>Nominee Details ({nominees.length})</span>
            </div>
          </div>
          <div class="card-body-styled">
            <div class="nominees-grid">
              {#each nominees as nom, idx}
                <div class="nominee-card">
                  <div class="nominee-header">
                    <span class="nominee-name">{nom.name || 'Nominee ' + (idx + 1)}</span>
                    <span class="nominee-share-badge">{nom.percentage_of_share || '100'}% Share</span>
                  </div>
                  <div class="kv-list margin-top-half">
                    <div class="kv-item">
                      <span class="kv-label">Relationship</span>
                      <span class="kv-value">{nom.relationship || '—'}</span>
                    </div>
                    <div class="kv-item">
                      <span class="kv-label">ID Number</span>
                      <span class="kv-value">{nom.id_number || '—'} ({nom.id_type || 'ID'})</span>
                    </div>
                    <div class="kv-item">
                      <span class="kv-label">Mobile</span>
                      <span class="kv-value">{nom.mobile || '—'}</span>
                    </div>
                    <div class="kv-item">
                      <span class="kv-label">Email</span>
                      <span class="kv-value">{nom.email || '—'}</span>
                    </div>
                    <div class="kv-item stacked">
                      <span class="kv-label">Address</span>
                      <span class="kv-value address-text">{nom.address || '—'}</span>
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {/if}

    </div>
  </div>

  <!-- Reusable UCC Tag Assignment Modal -->
  <AssignUccTagModal
    bind:opened={assignModalOpened}
    user={user_details}
    lockUser={true}
    initialTag={user_details.ucc || user_details.client_tag || ""}
    initialMultiplier={user_details.multiplier || 1.0}
    initialMasterOptIn={Boolean(user_details.master_opt_in)}
    environment="LIVE"
    on:success={handleUccAssigned}
  />

  <!-- FCML KYC Dossier Popup Modal -->
  <Popup opened={dossierModalOpened} onPopupClosed={() => (dossierModalOpened = false)} class="dossier-popup">
    <Page>
      <Navbar title="FCML Client KYC Dossier Summary">
        <NavRight slot="right">
          <Link popupClose text="Close" />
        </NavRight>
      </Navbar>

      <Block class="dossier-modal-content">
        <div class="dossier-header-bar">
          <div>
            <h3 class="dossier-client-name">{user_details.fullName || user_details.mobile}</h3>
            <span class="dossier-client-sub">UCC: {user_details.ucc || "UNASSIGNED"} • Mobile: +91 {user_details.mobile}</span>
          </div>
          <div class="dossier-dl-actions">
            <Button small fill color="blue" on:click={downloadDossierTxt}>
              <span>📥 Download .TXT</span>
            </Button>
            <Button small outline color="blue" on:click={downloadDossierJson}>
              <span>📥 Download .JSON</span>
            </Button>
          </div>
        </div>

        <div class="dossier-sections-grid margin-top">
          <div class="dossier-sec">
            <h4>1. Depository & Demat (CDSL)</h4>
            <div class="sec-row"><span>Depository:</span> <strong>{DEPOSITORY_NAME}</strong></div>
            <div class="sec-row"><span>Participant:</span> <strong>{FCML_DP_NAME}</strong></div>
            <div class="sec-row"><span>DP ID:</span> <code>{FCML_DP_ID}</code></div>
            <div class="sec-row"><span>16-Digit Demat:</span> <strong class="demat-txt">{formatDematDisplay(calculateDematAccount(user_details.demat_acc_number || user_details.trdr_id || user_details.demat_id || user_details.ucc))}</strong></div>
          </div>

          <div class="dossier-sec">
            <h4>2. Banking & Penny Drop</h4>
            <div class="sec-row"><span>Bank:</span> <strong>{user_details.bank_name || '-'}</strong></div>
            <div class="sec-row"><span>Account No:</span> <code>{user_details.account_number || '-'}</code> ({user_details.account_type || 'Savings'})</div>
            <div class="sec-row"><span>IFSC:</span> <code>{user_details.ifsc || '-'}</code></div>
            <div class="sec-row"><span>Penny Drop:</span> <StatusBadge status={user_details.penny_drop_status === true || user_details.penny_drop_status === 'verified' ? 'APPROVED' : 'PENDING'} size="small" /></div>
          </div>

          <div class="dossier-sec full-width">
            <h4>3. Decoded Demographic & Compliance Summary</h4>
            <div class="sec-row"><span>PAN:</span> <code>{user_details.pan || '-'}</code></div>
            <div class="sec-row"><span>DOB:</span> <span>{dob || user_details.dob || '-'}</span></div>
            <div class="sec-row"><span>Gender:</span> <span>{user_details.gender || '-'}</span></div>
            <div class="sec-row"><span>Permanent Address:</span> <span>{formatDecodedAddress(user_details.permanentAddress)}</span></div>
            <div class="sec-row"><span>Current Address:</span> <span>{formatDecodedAddress(user_details.communicationAddress)}</span></div>
            <div class="sec-row"><span>Agreement Status:</span> <StatusBadge status={isAgreementSigned ? 'APPROVED' : 'PENDING'} text={isAgreementSigned ? 'SIGNED' : 'UNSIGNED'} size="small" /></div>
          </div>
        </div>
      </Block>
    </Page>
  </Popup>
</Page>

<style>
  @import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap");

  :global(.font-montserrat) {
    font-family: Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }

  .user-details-scroll-content {
    background-color: #f8fafc;
    min-height: calc(100vh - 44px);
    padding-bottom: 40px;
  }

  .user-details-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 16px;
    box-sizing: border-box;
  }

  /* Responsive Grid System */
  .top-cards-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  @media (min-width: 768px) {
    .top-cards-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .overview-card {
      grid-column: span 2;
    }
  }

  @media (min-width: 1100px) {
    .top-cards-grid {
      grid-template-columns: repeat(3, 1fr);
    }
    .overview-card {
      grid-column: span 1;
    }
  }

  /* Middle Grid: 1 column on phone, 2 on tablet, 4 on desktop */
  .details-cards-grid {
    display: grid;
    grid-template-columns: 1fr; /* 1 box on mobile! */
    gap: 16px;
  }

  @media (min-width: 640px) {
    .details-cards-grid {
      grid-template-columns: repeat(2, 1fr); /* 2 boxes on tablet/phablet */
    }
  }

  @media (min-width: 1200px) {
    .details-cards-grid {
      grid-template-columns: repeat(4, 1fr); /* 4 boxes on desktop */
    }
  }

  /* Modern Card Styling */
  .modern-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .modern-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .card-header-styled {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    background: #ffffff;
  }

  .header-title-flex {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .header-icon-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
  }
  .header-icon-box.blue { background: #e0f2fe; }
  .header-icon-box.purple { background: #f3e8ff; }
  .header-icon-box.green { background: #dcfce7; }
  .header-icon-box.orange { background: #ffedd5; }
  .header-icon-box.teal { background: #ccfbf1; }

  .card-body-styled {
    padding: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Overview Card Specifics */
  .overview-header-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid #f1f5f9;
  }

  .avatar-container {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
  }

  .avatar-img {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    object-fit: cover;
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  }

  .avatar-fallback {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: linear-gradient(135deg, #0284c7, #0369a1);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 2px 6px rgba(2, 132, 199, 0.25);
  }

  .overview-title-col {
    min-width: 0;
    flex: 1;
  }

  .user-name-heading {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .overview-subinfo {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 12px;
    color: #64748b;
  }

  .subinfo-item {
    display: inline-flex;
    align-items: center;
  }

  .mini-tag {
    background: #f1f5f9;
    color: #475569;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    margin-right: 4px;
  }

  .overview-controls-list {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .control-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .control-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
  }

  .quick-approve-container {
    margin-top: 2px;
  }

  .quick-approve-button {
    width: 100%;
    height: 32px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(22, 163, 74, 0.25);
    transition: background-color 0.15s ease;
  }
  .quick-approve-button:hover {
    background: #15803d;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 12px;
  }
  .status-badge.signed {
    background: #dcfce7;
    color: #15803d;
  }
  .status-badge.unsigned {
    background: #fee2e2;
    color: #b91c1c;
  }
  .status-badge.pending {
    background: #fef3c7;
    color: #b45309;
  }

  .danger-zone-footer {
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px dashed #e2e8f0;
  }

  .soft-delete-btn {
    width: 100%;
    background: #fff;
    border: 1px solid #fca5a5;
    color: #dc2626;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }
  .soft-delete-btn:hover {
    background: #fee2e2;
    border-color: #f87171;
  }

  /* Key-Value Lists */
  .kv-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .kv-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    font-size: 13px;
    line-height: 1.4;
  }

  .kv-item.stacked {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .kv-label {
    color: #64748b;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
  }

  .kv-value {
    color: #0f172a;
    font-weight: 500;
    text-align: right;
    word-break: break-word;
    overflow-wrap: anywhere;
    min-width: 0;
  }

  .kv-item.stacked .kv-value {
    text-align: left;
    width: 100%;
  }

  .email-value {
    word-break: break-all;
    overflow-wrap: anywhere;
    font-size: 12px;
  }

  .address-text {
    font-size: 12px;
    color: #334155;
    line-height: 1.45;
  }

  .monospace-pill {
    background: #f1f5f9;
    color: #334155;
    padding: 2px 7px;
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.3px;
    display: inline-block;
    word-break: break-all;
  }

  /* Demat Input Group */
  .demat-input-group {
    display: flex;
    gap: 8px;
    width: 100%;
    align-items: center;
    margin-top: 4px;
  }

  .demat-input {
    flex: 1 1 auto !important;
    width: 0 !important;
    min-width: 0 !important;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 12px;
    background: #ffffff;
    color: #0f172a;
    box-sizing: border-box;
  }
  .demat-input:focus {
    border-color: #0284c7;
    outline: none;
    box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
  }

  .save-demat-btn {
    width: auto !important;
    flex: 0 0 auto !important;
    min-width: 60px;
    padding: 0 14px;
    height: 32px;
    line-height: 32px;
    background: #0284c7;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    transition: background-color 0.15s ease;
  }
  .save-demat-btn:hover {
    background: #0369a1;
  }
  .save-demat-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* UCC Tag Actions */
  .ucc-tag-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .ucc-active-badge {
    background: #e0f2fe;
    color: #0284c7;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
  }

  .ucc-unassigned-badge {
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fecaca;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .manage-tag-btn {
    width: auto !important;
    font-size: 11px;
    height: 26px;
    padding: 0 8px;
    border: 1px solid #0284c7;
    background: transparent;
    color: #0284c7;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .manage-tag-btn:hover {
    background: #f0f9ff;
  }

  .geo-link {
    color: #0284c7;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    font-size: 12px;
  }
  .geo-link:hover {
    text-decoration: underline;
  }

  /* Document Card Wrapper */
  .documents-card-body {
    padding: 10px 14px;
  }

  .documents-list-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  /* Nominees Grid */
  .nominees-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (min-width: 768px) {
    .nominees-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .nominee-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px;
  }

  .nominee-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  .nominee-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .nominee-share-badge {
    background: #f3e8ff;
    color: #7e22ce;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 10px;
  }

  .url-badge {
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
  }

  .broker-desk-card {
    border-left: 4px solid #8b5cf6;
  }

  .desk-badge {
    background: #ede9fe;
    color: #7c3aed;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 6px;
    text-transform: uppercase;
  }

  .broker-desk-desc {
    font-size: 13px;
    color: #475569;
    margin: 0 0 12px 0;
    line-height: 1.5;
  }

  .broker-actions-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  :global(.dossier-popup) {
    --f7-popup-tablet-width: 780px;
  }

  .dossier-modal-content {
    padding: 16px;
  }

  .dossier-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 16px;
  }

  .dossier-client-name {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .dossier-client-sub {
    font-size: 13px;
    color: #64748b;
  }

  .dossier-dl-actions {
    display: flex;
    gap: 8px;
  }

  .dossier-sections-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .dossier-sec {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .dossier-sec.full-width {
    grid-column: 1 / -1;
  }

  .dossier-sec h4 {
    margin: 0 0 6px 0;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    color: #475569;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 6px;
  }

  .sec-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    gap: 8px;
  }

  .demat-txt {
    font-family: monospace;
    color: #16a34a;
  }

  @media (max-width: 640px) {
    .dossier-sections-grid {
      grid-template-columns: 1fr;
    }
    .hide-on-mobile {
      display: none !important;
    }
    .user-details-container {
      padding: 10px;
    }
  }
</style>
